import { v4 as uuidV4 } from 'uuid'
import { identity } from 'ramda'
import {
  NOTIFICATION_SUCCESS,
  NOTIFICATION_ERROR,
  NOTIFICATION_DEFAULT_TIME,
  NOTIFICATION_DEFAULT_ERROR_TIME,
  NOTIFICATION_INLINE,
} from 'utils/constants.js'
import { app } from 'services'
// Import the leaf module directly to avoid a circular dependency:
// actions/application → sagas/errorHandlers barrel → individual handlers
// → actions/application (displayErrorModal). pageErrorHandler itself only
// imports from sagas/errorHandlers/constants, so the direct import is cycle-free.
import pageErrorHandler from 'sagas/errorHandlers/pageErrorHandler'
import { createAction } from './utils'

export const TOGGLE_USELESS_VARIABLE = '@@chec/TOGGLE_USELESS_VARIABLE'
export const toggleUselessVariable = () => (dispatch) => {
  // TODO: We cannot call this immediately because we want to make sure the location change reducer is done. This is hacky as fuck.
  setTimeout(
    () =>
      dispatch({
        type: TOGGLE_USELESS_VARIABLE,
      }),
    10
  )
}

export const ASYNC_DISPATCH = 'ASYNC_DISPATCH'
export const addToAsyncSet = ({ signal }) => ({
  type: ASYNC_DISPATCH,
  signal,
})

export const ASYNC_RESOLVE = 'ASYNC_RESOLVE'
export const removeFromAsyncSet = ({ signal }) => ({
  type: ASYNC_RESOLVE,
  signal,
})

export const CREATE_SYSTEM_NOTIFICATION = 'CREATE_SYSTEM_NOTIFICATION'
const createNotification = ({
  notificationMessage,
  notificationType,
  persistent,
  notificationId,
  async,
  closable,
  description,
  redirect,
  actionTrigger,
  reportLink,
  notificationFormat,
  showIcon,
  icon,
  hideOnPaths,
}) => ({
  notificationMessage,
  notificationType,
  notificationFormat,
  persistent,
  notificationId,
  async,
  closable,
  description,
  redirect,
  actionTrigger,
  reportLink,
  type: CREATE_SYSTEM_NOTIFICATION,
  showIcon,
  icon,
  hideOnPaths,
})

export const DELETE_SYSTEM_NOTIFICATION = 'DELETE_SYSTEM_NOTIFICATION'
export const deleteSystemNotification = ({ notificationId }) => ({
  id: notificationId,
  type: DELETE_SYSTEM_NOTIFICATION,
})

export const createSystemNotification =
  ({
    notificationMessage,
    notificationType = NOTIFICATION_SUCCESS,
    persistent = false, // true for banner notification, default to toast notifications
    notificationId = uuidV4(), // auto id for toast notification, constant for banner notification
    async,
    closable,
    description,
    redirect,
    actionTrigger,
    reportLink,
    notificationFormat = NOTIFICATION_INLINE,
    showIcon,
    icon,
    hideOnPaths,
  }) =>
  (dispatch) => {
    dispatch(
      createNotification({
        notificationMessage,
        notificationType,
        persistent,
        notificationId,
        async,
        closable,
        description,
        redirect,
        actionTrigger,
        reportLink,
        notificationFormat,
        showIcon,
        icon,
        hideOnPaths,
      })
    )

    if (!persistent && !closable) {
      return new Promise((res, rej) => {
        try {
          setTimeout(
            () => {
              dispatch(deleteSystemNotification({ notificationId }))
              res()
              // Errors are generally more informative and important.
            },
            notificationType === NOTIFICATION_ERROR
              ? NOTIFICATION_DEFAULT_TIME
              : NOTIFICATION_DEFAULT_ERROR_TIME
          )
        } catch (err) {
          rej(err)
        }
      })
    }

    return Promise.resolve()
  }

export const DISPLAY_ERROR_MODAL = 'DISPLAY_ERROR_MODAL'
export const displayErrorModal = (errorCode, errorMessages) => ({
  errorCode,
  errorMessages,
  type: DISPLAY_ERROR_MODAL,
})

export const HIDE_ERROR_MODAL = 'HIDE_ERROR_MODAL'
export const hideErrorModal = () => ({
  type: HIDE_ERROR_MODAL,
})

export const GET_API_VERSION = 'GET_API_VERSION'
export const getAPIVersion = () =>
  apiAction({
    asyncFn: app.getAPIVersion,
    id: 'api version',
    successActionType: GET_API_VERSION,
    // Non-critical: failure only affects footer version display. Suppress the
    // blocking ErrorModal so the user can continue using the app normally.
    errorHandlers: [pageErrorHandler],
  })

export const API_ERROR = 'API_ERROR'
export const API_CALL = 'API_CALL'
/**
 * Creates an API_CALL action that is intercepted by the `watchApiCall` saga.
 *
 * On failure, errors are routed through an ordered handler chain. A default
 * handler is always appended last and shows the generic ErrorModal. Pass
 * `errorHandlers` to insert domain-specific handlers before the default.
 *
 * See `sagas/errorHandlers/index.js` for the full chain documentation and a
 * guide to writing handlers.
 *
 * @param {Function}   asyncFn            - The service function to call (required).
 * @param {object}     [args]             - Arguments forwarded to asyncFn.
 * @param {string}     successActionType  - Action type dispatched on success (required).
 * @param {string}     [id]               - Suffix appended to callSignal for de-duplication.
 * @param {string}     [failureActionType]- Action type dispatched on failure (for reducers).
 * @param {string}     [requestActionType]- Action type dispatched before the call starts.
 * @param {object}     [schema]           - Normalizr schema (deprecated — do not use for new code).
 * @param {Function}   [resolve]          - Called on success (for promiseApiAction).
 * @param {Function}   [reject]           - Called on failure (for promiseApiAction).
 * @param {Function}   [conditionalSelector] - If this selector returns truthy, the call is skipped.
 * @param {Generator[]} [errorHandlers]   - Ordered array of error handler generators inserted
 *                                          before the default handler. See errorHandlers/index.js.
 */
export const apiAction = ({
  asyncFn,
  args,
  successActionType,
  id,
  failureActionType,
  requestActionType,
  schema,
  resolve,
  reject,
  conditionalSelector,
  errorHandlers,
}) => {
  if (!asyncFn || !successActionType)
    throw new Error(`apiAction is missing required property`)
  return {
    type: API_CALL,
    asyncFn,
    args,
    successActionType,
    id,
    failureActionType,
    requestActionType,
    schema, // normalizr schema — deprecated, do not use for new code
    resolve,
    reject,
    conditionalSelector,
    errorHandlers, // optional — see sagas/errorHandlers/index.js
  }
}

/**
 * Wraps `apiAction` in a Promise so callers can `await` the result or chain `.then()`.
 *
 * ⚠ DE-DUPLICATION: if an identical call (same callSignal) is already in-flight,
 * apiCall.js resolves this Promise with `undefined` rather than waiting for the
 * first call to finish. Every caller MUST guard against this:
 *
 *   const result = await dispatch(promiseApiAction(...))
 *   if (!result) return   // de-dup: call was already in-flight, no-op
 *
 * Callers that use `.then(result => ...)` must also guard:
 *
 *   .then(result => {
 *     if (!result) return   // de-dup guard
 *     // safe to use result here
 *   })
 *
 * Failing to guard means side-effects (close modal, show success toast, etc.)
 * will fire even when no API call was made, which can corrupt UI state.
 *
 * The resolved value is the Redux SUCCESS ACTION OBJECT ({ type, data, ...args }),
 * NOT the raw service response. Read response data via `result?.data`.
 */
export const promiseApiAction = (apiActionParams) => (dispatch) => {
  const {
    resolve: resolveCallback = identity,
    reject: rejectCallback = identity,
  } = apiActionParams

  return new Promise((resolve, reject) =>
    dispatch(
      apiAction({
        ...apiActionParams,
        resolve: (result) => {
          // result is the Redux SUCCESS ACTION OBJECT ({ type, data, ...args }),
          // NOT the raw service response. apiCall.js constructs this as:
          //   { type: successActionType, ...args, data: responseData }
          // Callers should read response data via result?.data, e.g.:
          //   const data = result?.data ?? result   (guards against de-dup undefined)
          // De-dup: when a duplicate in-flight call is rejected, result === undefined
          // and the Promise resolves immediately. Callers must guard: result?.data.
          resolveCallback(result)
          resolve(result)
        },
        reject: (err) => {
          rejectCallback(err)
          reject(err)
        },
      })
    )
  )
}

// when creating an action that reqauires user input, the initial action should be created with this function
// which will provide the necessary data to produce signals in the container
export const apiActionStub = ({
  asyncFn,
  args,
  successActionType,
  id,
  failureActionType,
  requestActionType,
  schema,
}) => ({
  type: null, // constant
  asyncFn, // required
  args, // optional
  successActionType, // required
  id, // required
  failureActionType, // optional
  requestActionType, // optional
  schema, // optional
})

// the partial api action that was created in mapDispatchToProps should be completed in the handler with this function
// which will marry the user produced args with the initial args and update the type to a functioning action
// for our purposes
export const completeApiActionStub = (initialAction, userArgs) => {
  const args = { ...initialAction.args, ...userArgs }

  return {
    ...initialAction,
    type: API_CALL,
    args,
  }
}

// iterate over action objects (full or stubbed) and create signals for tracking error/loading states
export const createSignals = (actionsDictionary) =>
  Object.entries(actionsDictionary).reduce(
    (accum, [key, { successActionType, id }]) => ({
      ...accum,
      ...{ [key]: `${successActionType}${id || ''}` },
    }),
    {}
  )

export const SELECTED_APP_SECTION = 'SELECTED_APP_SECTION'

export const setSelectedAppSection = (selectedSection) => {
  const { selection } = selectedSection

  return {
    selectedSection: selection,
    type: SELECTED_APP_SECTION,
  }
}

export const HAS_SELECTED_APP_SECTION = 'HAS_SELECTED_APP_SECTION'
export const setHasSelectedAppSection = createAction(HAS_SELECTED_APP_SECTION, [
  'isSelected',
])

export const HANDLE_NOTIFICATIONS_PANEL = 'HANDLE_NOTIFICATIONS_PANEL'
export const handleNotificationPanelState = createAction(
  HANDLE_NOTIFICATIONS_PANEL,
  ['isExpanded']
)
